/**
 * DicePurchaseModal — the "tap a store die → unlock it" confirm sheet.
 *
 * Shows the die over a base plate with its rarity, then two pay buttons:
 * gold or a single dice token. A button is disabled when the player can't
 * afford it (with an inline "Not enough" hint). On confirm it emits `buy`;
 * the orchestrator runs the server purchase and either closes (success →
 * celebration) or calls showError() + setBusy(false).
 *
 * Body-level singleton with injected CSS, mirroring DiceUnlockedModal. Sits
 * below the celebration (z 825) at z 820 so the confetti lands on top.
 */

import { EventEmitter } from '../../engine/core/EventEmitter';

const STYLE_ID = 'sdl-dicepurchase-styles';
const FALLBACK_THUMB = '/assets/ui/dice-thumb-placeholder.png';
const BASE_PLATE = '/assets/ui/dice-base.png';

export interface DicePurchaseOptions {
  diceId: string;
  thumbnailUrl: string;
  rarity?: string;
  goldPrice: number;
  tokenPrice: number;
  /** When true, hide the gold button — this die is a token-only unlock. */
  tokenOnly?: boolean;
  goldBalance: number;
  tokenBalance: number;
}

interface DicePurchaseEvents {
  buy: { currency: 'gold' | 'token' };
  try: undefined;
  close: undefined;
}

/** Dice-token icon (die face with 5 pips), matching the store wallet pill. */
const TOKEN_ICON_SVG = `
  <svg class="sdl-dicepurchase-tokenico" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <rect x="3.5" y="3.5" width="17" height="17" rx="4" fill="rgba(255,255,255,0.95)"/>
    <circle cx="8.5" cy="8.5" r="1.6" fill="#a00050"/>
    <circle cx="15.5" cy="8.5" r="1.6" fill="#a00050"/>
    <circle cx="8.5" cy="15.5" r="1.6" fill="#a00050"/>
    <circle cx="15.5" cy="15.5" r="1.6" fill="#a00050"/>
    <circle cx="12" cy="12" r="1.6" fill="#a00050"/>
  </svg>`;

export class DicePurchaseModal extends EventEmitter<DicePurchaseEvents> {
  public element: HTMLElement;
  private thumbImg: HTMLImageElement;
  private rarityEl: HTMLElement;
  private goldBtn: HTMLButtonElement;
  private tokenBtn: HTMLButtonElement;
  private tryBtn: HTMLButtonElement;
  private errorEl: HTMLElement;

  constructor() {
    super();
    DicePurchaseModal.injectStyles();
    this.element = document.createElement('div');
    this.element.className = 'sdl-dicepurchase-host';
    this.element.innerHTML = `
      <div class="sdl-dicepurchase-card" role="dialog" aria-modal="true">
        <button class="sdl-dicepurchase-close" aria-label="Close">&times;</button>
        <div class="sdl-dicepurchase-heading">UNLOCK DICE</div>
        <div class="sdl-dicepurchase-rarity"></div>
        <div class="sdl-dicepurchase-stage">
          <img class="sdl-dicepurchase-base" src="${BASE_PLATE}" alt="" onerror="this.style.display='none'">
          <img class="sdl-dicepurchase-thumb" src="" alt="">
        </div>
        <button class="sdl-dicepurchase-try">TRY THESE DICE</button>
        <div class="sdl-dicepurchase-error" hidden></div>
        <div class="sdl-dicepurchase-buttons">
          <button class="sdl-dicepurchase-btn gold">
            <span class="sdl-dicepurchase-btn-main"><img class="sdl-dicepurchase-goldico" src="/assets/icons/currency/gold.svg" alt=""> <b></b></span>
            <span class="sdl-dicepurchase-btn-sub"></span>
          </button>
          <button class="sdl-dicepurchase-btn token">
            <span class="sdl-dicepurchase-btn-main">${TOKEN_ICON_SVG} <b></b></span>
            <span class="sdl-dicepurchase-btn-sub"></span>
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(this.element);

    this.thumbImg = this.element.querySelector('.sdl-dicepurchase-thumb') as HTMLImageElement;
    this.rarityEl = this.element.querySelector('.sdl-dicepurchase-rarity') as HTMLElement;
    this.goldBtn = this.element.querySelector('.sdl-dicepurchase-btn.gold') as HTMLButtonElement;
    this.tokenBtn = this.element.querySelector('.sdl-dicepurchase-btn.token') as HTMLButtonElement;
    this.tryBtn = this.element.querySelector('.sdl-dicepurchase-try') as HTMLButtonElement;
    this.errorEl = this.element.querySelector('.sdl-dicepurchase-error') as HTMLElement;

    this.goldBtn.addEventListener('click', () => {
      if (this.goldBtn.disabled) return;
      this.emit('buy', { currency: 'gold' });
    });
    this.tokenBtn.addEventListener('click', () => {
      if (this.tokenBtn.disabled) return;
      this.emit('buy', { currency: 'token' });
    });
    this.tryBtn.addEventListener('click', () => {
      if (this.tryBtn.disabled) return;
      this.emit('try', undefined);
    });
    this.element.querySelector('.sdl-dicepurchase-close')!
      .addEventListener('click', () => this.close());
    // Tap the dimmed backdrop to dismiss.
    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) this.close();
    });
  }

  show(opts: DicePurchaseOptions): void {
    this.thumbImg.src = opts.thumbnailUrl;
    this.thumbImg.onerror = () => {
      this.thumbImg.onerror = null;
      this.thumbImg.src = FALLBACK_THUMB;
    };

    if (opts.rarity) {
      this.rarityEl.textContent = opts.rarity.toUpperCase();
      this.rarityEl.hidden = false;
    } else {
      this.rarityEl.hidden = true;
    }

    this.hideError();
    this.setBusy(false);

    // Token-only dice (Exotic) drop the gold button entirely — a token is the
    // only way in, so a greyed-out gold button would just read as confusing.
    const tokenOnly = !!opts.tokenOnly;
    this.goldBtn.hidden = tokenOnly;

    const canGold = !tokenOnly && opts.goldBalance >= opts.goldPrice;
    const canToken = opts.tokenBalance >= opts.tokenPrice;

    (this.goldBtn.querySelector('b') as HTMLElement).textContent = opts.goldPrice.toLocaleString();
    (this.goldBtn.querySelector('.sdl-dicepurchase-btn-sub') as HTMLElement).textContent =
      canGold ? 'GOLD' : 'Not enough gold';
    this.goldBtn.disabled = tokenOnly || !canGold;
    this.goldBtn.classList.toggle('insufficient', !tokenOnly && !canGold);

    const tokenLabel = opts.tokenPrice === 1 ? 'TOKEN' : 'TOKENS';
    (this.tokenBtn.querySelector('b') as HTMLElement).textContent = String(opts.tokenPrice);
    (this.tokenBtn.querySelector('.sdl-dicepurchase-btn-sub') as HTMLElement).textContent =
      canToken ? tokenLabel : 'Not enough tokens';
    this.tokenBtn.disabled = !canToken;
    this.tokenBtn.classList.toggle('insufficient', !canToken);

    this.element.classList.add('visible');
  }

  /** Lock the buttons while the server purchase is in flight. */
  setBusy(busy: boolean): void {
    this.element.classList.toggle('busy', busy);
    this.goldBtn.disabled = busy || this.goldBtn.classList.contains('insufficient');
    this.tokenBtn.disabled = busy || this.tokenBtn.classList.contains('insufficient');
    this.tryBtn.disabled = busy;
  }

  showError(message: string): void {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  private hideError(): void {
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  isVisible(): boolean {
    return this.element.classList.contains('visible');
  }

  close(): void {
    if (!this.isVisible()) return;
    this.element.classList.remove('visible');
    this.emit('close', undefined);
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-dicepurchase-host {
        position: fixed; inset: 0; z-index: 820;
        display: none; align-items: center; justify-content: center;
        padding: 24px;
        background: rgba(4, 4, 8, 0.78);
        backdrop-filter: blur(7px); -webkit-backdrop-filter: blur(7px);
      }
      .sdl-dicepurchase-host.visible { display: flex; }

      .sdl-dicepurchase-card {
        position: relative;
        display: flex; flex-direction: column; align-items: center; gap: 14px;
        max-width: min(92vw, 380px); width: 100%;
        padding: 26px 22px 22px;
        background: linear-gradient(180deg, #15151c, #0c0c11);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 20px;
        box-shadow: 0 24px 60px rgba(0, 0, 0, 0.7);
        /* fill-mode 'both' => the pre-start frame shows the 0% keyframe
         * (opacity 0) instead of the base style, killing the Android WebView
         * one-frame flash-then-blink on open. See DiceUnlockedModal for note. */
        animation: sdl-dicepurchase-in 0.3s cubic-bezier(0.34, 1.4, 0.6, 1) both;
      }
      @keyframes sdl-dicepurchase-in {
        from { transform: translateY(24px) scale(0.92); opacity: 0; }
        to   { transform: translateY(0) scale(1); opacity: 1; }
      }

      .sdl-dicepurchase-close {
        position: absolute; top: 8px; right: 12px;
        background: transparent; border: none;
        color: rgba(255, 255, 255, 0.5);
        font-size: 26px; line-height: 1; cursor: pointer;
        -webkit-tap-highlight-color: transparent;
      }
      .sdl-dicepurchase-close:active { color: #fff; }

      .sdl-dicepurchase-heading {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 30px; letter-spacing: 2px;
        color: var(--gold, #FFD700);
        text-shadow: 0 0 18px rgba(255, 215, 0, 0.45);
      }

      .sdl-dicepurchase-rarity {
        font-family: 'Rajdhani', sans-serif;
        font-size: 13px; font-weight: 700; letter-spacing: 3px;
        color: var(--cyan, #00F5FF);
        margin-top: -6px;
      }

      .sdl-dicepurchase-stage {
        position: relative; width: 9rem; height: 9.6rem;
        display: flex; align-items: flex-end; justify-content: center;
      }
      .sdl-dicepurchase-base {
        position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);
        width: 9rem; height: auto; opacity: 0.9; pointer-events: none;
      }
      .sdl-dicepurchase-thumb {
        position: relative; width: 6.8rem; height: auto; margin-bottom: 1rem;
        filter: drop-shadow(2px 3px 4px rgba(0, 0, 0, 0.9));
        pointer-events: none;
      }

      .sdl-dicepurchase-try {
        margin-top: 2px;
        padding: 9px 26px;
        background: transparent;
        border: 1px solid var(--cyan, #00F5FF);
        border-radius: 999px;
        color: var(--cyan, #00F5FF);
        font-family: 'Bebas Neue', sans-serif;
        font-size: 18px; letter-spacing: 1.5px;
        cursor: pointer; -webkit-tap-highlight-color: transparent;
        transition: transform 0.12s ease, background 0.15s ease;
      }
      .sdl-dicepurchase-try:active { transform: scale(0.96); background: rgba(0, 245, 255, 0.12); }
      .sdl-dicepurchase-try:disabled { opacity: 0.4; pointer-events: none; }

      .sdl-dicepurchase-tokenico { vertical-align: middle; }

      .sdl-dicepurchase-error {
        font-family: 'Rajdhani', sans-serif;
        font-size: 13px; font-weight: 600;
        color: var(--red, #FF3366);
        text-align: center;
      }

      .sdl-dicepurchase-buttons {
        display: flex; gap: 12px; width: 100%; margin-top: 2px;
      }
      .sdl-dicepurchase-btn {
        flex: 1;
        display: flex; flex-direction: column; align-items: center; gap: 2px;
        padding: 12px 8px;
        border: 1px solid rgba(255, 255, 255, 0.14);
        border-radius: 14px;
        cursor: pointer; -webkit-tap-highlight-color: transparent;
        transition: transform 0.12s ease, filter 0.15s ease, opacity 0.15s ease;
      }
      /* The class sets display:flex, which would override the [hidden] UA rule;
       * re-assert display:none for token-only dice that drop the gold button. */
      .sdl-dicepurchase-btn[hidden] { display: none; }
      .sdl-dicepurchase-btn:active { transform: scale(0.96); }
      .sdl-dicepurchase-btn.gold {
        background: linear-gradient(180deg, #ffe169, #f0b400);
        color: #1a1208;
        box-shadow: 0 6px 18px rgba(240, 180, 0, 0.4);
      }
      .sdl-dicepurchase-btn.token {
        background: linear-gradient(180deg, #2a2a36, #1a1a22);
        color: #fff;
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
      }
      .sdl-dicepurchase-btn.insufficient {
        filter: grayscale(0.7) brightness(0.7);
        opacity: 0.6; cursor: default;
      }
      .sdl-dicepurchase-btn:disabled { cursor: default; }
      .sdl-dicepurchase-btn-main {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 24px; letter-spacing: 1px;
        display: inline-flex; align-items: center; gap: 4px;
      }
      .sdl-dicepurchase-goldico { width: 18px; height: 18px; }
      .sdl-dicepurchase-btn-sub {
        font-family: 'Rajdhani', sans-serif;
        font-size: 11px; font-weight: 700; letter-spacing: 1.5px;
        opacity: 0.78;
      }

      .sdl-dicepurchase-host.busy .sdl-dicepurchase-card { pointer-events: none; }
      .sdl-dicepurchase-host.busy .sdl-dicepurchase-buttons { opacity: 0.6; }
    `;
    document.head.appendChild(style);
  }
}
